/**
 * API Service
 * Centralized API communication layer
 * Handles all HTTP requests to backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sambas-energy-backend.onrender.com/api';

class APIError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.statusCode = statusCode;
    this.name = 'APIError';
  }
}

/**
 * Generic fetch wrapper with error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || 'API request failed',
        response.status
      );
    }

    return data;
  } catch (err) {
    if (err instanceof APIError) throw err;
    throw new APIError(err.message, 0);
  }
}

/**
 * Signup API Methods
 */
const signup = {
  /**
   * Create new email signup
   * @param {string} email - User email
   * @param {string} product - Product type
   * @returns {Promise<Object>} Signup response
   */
  async create(email, product) {
    return fetchAPI('/signup', {
      method: 'POST',
      body: JSON.stringify({ email, product })
    });
  },

  /**
   * Get all signups
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Signups list
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return fetchAPI(`/signups?${params}`);
  },

  /**
   * Get signup statistics
   * @returns {Promise<Object>} Signup stats
   */
  async getStats() {
    return fetchAPI('/signups/stats');
  }
};

/**
 * Click Tracking API Methods
 */
const click = {
  /**
   * Track a button click
   * @param {string} product - Product type
   * @returns {Promise<Object>} Click tracking response
   */
  async track(product) {
    return fetchAPI('/track-click', {
      method: 'POST',
      body: JSON.stringify({ product })
    });
  },

  /**
   * Get all clicks
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Clicks list
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return fetchAPI(`/clicks?${params}`);
  },

  /**
   * Get click statistics
   * @returns {Promise<Object>} Click stats
   */
  async getStats() {
    return fetchAPI('/clicks/stats');
  }
};

/**
 * Purchase Attempt Tracking API Methods
 */
const purchaseAttempt = {
  /**
   * Track a purchase attempt (add to cart click)
   * @param {string} product - Product type (candy, apparel)
   * @param {string} size - Product size (60g, 30g)
   * @param {string} packSize - Pack size (single, 3, 6, 12, 24)
   * @param {number} price - Price of the item
   * @returns {Promise<Object>} Tracking response
   */
  async track(product, size, packSize, price) {
    return fetchAPI('/track-purchase-attempt', {
      method: 'POST',
      body: JSON.stringify({ product, size, packSize, price })
    });
  },

  /**
   * Get all purchase attempts
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Purchase attempts list
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return fetchAPI(`/purchase-attempts?${params}`);
  },

  /**
   * Get purchase attempt statistics
   * @returns {Promise<Object>} Purchase attempt stats
   */
  async getStats() {
    return fetchAPI('/purchase-attempts/stats');
  }
};

/**
 * Review API Methods
 */
const review = {
  /**
   * Submit a new review
   * @param {string} name - Reviewer name
   * @param {number} rating - Star rating (1-5)
   * @param {string} comment - Review comment
   * @returns {Promise<Object>} Created review
   */
  async create(name, rating, comment) {
    return fetchAPI('/reviews', {
      method: 'POST',
      body: JSON.stringify({ name, rating, comment })
    });
  },

  /**
   * Get all reviews
   * @returns {Promise<Object>} Reviews list
   */
  async getAll() {
    return fetchAPI('/reviews');
  }
};

export { signup, click, purchaseAttempt, review, APIError };
